/**
 * Hides in-doc edit affordances (MkDocs "Edit this page", GitLab /-/edit/, etc.)
 * unless /api/catalog-edit-check allows the same user as catalog edits
 * (ADMIN_GROUP direct member or direct GitLab project Developer+).
 * Reading TechDocs is unchanged.
 */

import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { configApiRef, fetchApiRef } from '@backstage/core-plugin-api';

const STYLE_ID = 'backstage-techdocs-edit-gate';

/** Hide common MkDocs Material + GitLab/GitHub doc edit entry points */
const HIDE_EDIT_CSS = `
a[href*="/-/edit/"],
a[href*="github.com"][href*="/edit/"],
a.md-content__button[href*="edit"],
a[title*="Edit this page" i],
a[title*="Edit on GitHub" i],
a[title*="Edit on GitLab" i] {
  display: none !important;
  visibility: hidden !important;
  pointer-events: none !important;
}
`;

function parseEntityRefFromPath(pathname: string): string | null {
  const standalone = pathname.match(/^\/docs\/([^/]+)\/([^/]+)\/([^/]+)(?:\/|$)/);
  if (standalone) {
    const [, ns, kind, name] = standalone;
    return `${kind.toLowerCase()}:${ns}/${name}`;
  }
  const embedded = pathname.match(
    /^\/catalog\/([^/]+)\/([^/]+)\/([^/]+)\/docs(?:\/|$)/,
  );
  if (embedded) {
    const [, ns, kind, name] = embedded;
    return `${kind.toLowerCase()}:${ns}/${name}`;
  }
  return null;
}

function isLikelyTechDocsShadowRoot(sr: ShadowRoot): boolean {
  return Boolean(
    sr.querySelector('.md-content, .md-container, .md-typeset, article.md-content__inner'),
  );
}

function findTechDocsShadowRoots(): ShadowRoot[] {
  const out: ShadowRoot[] = [];
  const seen = new Set<ShadowRoot>();
  document.querySelectorAll('*').forEach(el => {
    const sr = el.shadowRoot;
    if (sr && !seen.has(sr) && isLikelyTechDocsShadowRoot(sr)) {
      seen.add(sr);
      out.push(sr);
    }
  });
  return out;
}

function injectHide(sr: ShadowRoot): void {
  if (sr.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = HIDE_EDIT_CSS;
  sr.appendChild(style);
}

function removeHide(sr: ShadowRoot): void {
  sr.getElementById(STYLE_ID)?.remove();
}

/**
 * Runs `fn` immediately, on document body subtree mutations, and on a timer until the returned disposer runs.
 */
function watchBodySubtreeAndInterval(
  fn: () => void,
  intervalMs: number,
): () => void {
  fn();
  const obs = new MutationObserver(() => fn());
  obs.observe(document.body, { childList: true, subtree: true });
  const iv = window.setInterval(fn, intervalMs);
  return () => {
    obs.disconnect();
    window.clearInterval(iv);
  };
}

export function TechDocsShadowEditGate(): null {
  const location = useLocation();
  const fetchApi = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const [allowed, setAllowed] = useState<boolean | null>(null);

  const entityRef = useMemo(
    () => parseEntityRefFromPath(location.pathname),
    [location.pathname],
  );

  useEffect(() => {
    if (!entityRef) {
      setAllowed(null);
      return () => {};
    }
    let cancelled = false;
    const backendBase = config.getString('backend.baseUrl').replace(/\/$/, '');
    const url = `${backendBase}/api/catalog-edit-check?entityRef=${encodeURIComponent(entityRef)}`;
    fetchApi
      .fetch(url, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { allowed: false }))
      .then((b: { allowed?: boolean }) => {
        if (!cancelled) setAllowed(Boolean(b?.allowed));
      })
      .catch(() => {
        if (!cancelled) setAllowed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entityRef, fetchApi, config]);

  useEffect(() => {
    if (!entityRef) {
      return () => {};
    }

    const hideEdits = allowed !== true;

    const run = () => {
      const roots = findTechDocsShadowRoots();
      if (hideEdits) {
        roots.forEach(injectHide);
      } else {
        roots.forEach(removeHide);
      }
    };

    const stopWatching = watchBodySubtreeAndInterval(run, 900);

    return () => {
      stopWatching();
      findTechDocsShadowRoots().forEach(removeHide);
    };
  }, [entityRef, allowed]);

  return null;
}
